/**
 * DeepSearchProgress.jsx - Animated progress indicator for deep search operations
 * Shows steps like "Searching emails... 10" with animated dots
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Search, Mail, Users, Database, Loader2, CheckCircle2 } from 'lucide-react';

const stepIcons = {
    emails: Mail,
    contacts: Users,
    graph: Database,
    searching: Search,
    complete: CheckCircle2,
};

const stepLabels = {
    emails: 'Searching emails',
    contacts: 'Analyzing contacts',
    graph: 'Querying Knowledge Graph',
    searching: 'Searching',
    complete: 'Search complete',
};

const DeepSearchProgress = ({ steps = [], currentStep = null, darkMode = false }) => {
    if (!currentStep && steps.length === 0) return null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-xl p-4 space-y-2 ${darkMode
                    ? 'bg-slate-800/50 border border-white/5'
                    : 'bg-slate-50 border border-slate-200'
                }`}
        >
            {/* Completed steps */}
            {steps.map((step, idx) => {
                const Icon = stepIcons[step.type] || Search;
                return (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 size={14} className="text-green-500" />
                        <span className={`${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {stepLabels[step.type] || step.type}
                        </span>
                        {step.count && (
                            <span className={`font-mono text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                ({step.count})
                            </span>
                        )}
                    </div>
                );
            })}

            {/* Current step with animation */}
            {currentStep && (
                <div className="flex items-center gap-2 text-sm">
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                        <Loader2 size={14} className="text-blue-500" />
                    </motion.div>
                    <span className={darkMode ? 'text-white' : 'text-slate-700'}>
                        {stepLabels[currentStep.type] || currentStep.type}
                    </span>
                    {currentStep.count !== undefined && (
                        <motion.span
                            key={currentStep.count}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="font-mono text-blue-500 font-medium"
                        >
                            {currentStep.count}
                        </motion.span>
                    )}
                    {/* Animated dots */}
                    <span className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
                        <AnimatedDots />
                    </span>
                </div>
            )}
        </motion.div>
    );
};

// Animated dots component
const AnimatedDots = () => {
    return (
        <span className="inline-flex gap-0.5">
            {[0, 1, 2].map((i) => (
                <motion.span
                    key={i}
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{
                        duration: 1,
                        repeat: Infinity,
                        delay: i * 0.2,
                    }}
                >
                    .
                </motion.span>
            ))}
        </span>
    );
};

export default DeepSearchProgress;
